import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Lock, Mail, Loader2, AlertCircle, ArrowRight } from 'lucide-react';

const AdminLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setMessage(null);

        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });
 
        if (error) {
            setError(error.message);
            setLoading(false);
            return;
        }

        // Fetch user role for redirection
        const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', data.user.id)
            .single();

        if (profileError || !profile) {
            setError("Account verified, but no access role assigned. Contact Admin.");
            setLoading(false);
            return;
        }

        if (profile.role === 'ADMIN') {
            navigate('/admin');
        } else if (profile.role === 'BDE') {
            navigate('/sales');
        } else {
            setError("Access Restricted: Invalid role.");
            setLoading(false);
        }
    };

    const handleMagicLink = async () => {
        if (!email) {
            setError("Please enter your email first.");
            return;
        }
        setLoading(true);
        setError(null);
        setMessage(null);

        const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
                emailRedirectTo: window.location.origin + '/admin',
            }
        });

        if (error) {
            setError(error.message);
        } else {
            setMessage("Magic link sent! Check your email.");
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
            <div className="max-w-md w-full bg-white p-10 rounded-[3rem] shadow-2xl border border-slate-100 text-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-indigo-600"></div>
                
                <div className="mb-10 text-center flex flex-col items-center">
                    <img src="https://jaro-website.s3.ap-south-1.amazonaws.com/wp-content/uploads/2025/05/07171750/Jaro-Logo-website.svg" alt="Jaro Logo" className="h-12 w-auto mb-6" />
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter">UDAAN Console</h1>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-2">Admin Authority Access</p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600 animate-in fade-in slide-in-from-top-2">
                        <AlertCircle size={18} className="shrink-0" />
                        <p className="text-xs font-bold leading-relaxed">{error}</p>
                    </div>
                )}

                {message && (
                    <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3 text-emerald-600 animate-in fade-in slide-in-from-top-2">
                        <ArrowRight size={18} className="shrink-0" />
                        <p className="text-xs font-bold leading-relaxed">{message}</p>
                    </div>
                )}

                <form onSubmit={handleLogin} className="space-y-6">
                    <div className="relative group text-left">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-5 mb-1.5 block">Email Address</label>
                        <div className="relative">
                            <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-600 transition-colors" size={18} />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-4 pl-14 pr-6 font-bold text-sm outline-none focus:border-indigo-600 focus:bg-white transition-all shadow-sm"
                                placeholder="name@udaan.online"
                                required
                            />
                        </div>
                    </div>

                    <div className="relative group text-left">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-5 mb-1.5 block">Password</label>
                        <div className="relative">
                            <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-600 transition-colors" size={18} />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-4 pl-14 pr-6 font-bold text-sm outline-none focus:border-indigo-600 focus:bg-white transition-all shadow-sm"
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-5 bg-slate-900 text-white rounded-[2rem] font-black text-sm uppercase tracking-widest hover:bg-indigo-600 transition-all flex items-center justify-center gap-3 shadow-xl active:scale-95 disabled:opacity-50"
                    >
                        {loading ? <Loader2 className="animate-spin" size={20} /> : "Authenticate"}
                    </button>
                </form>

                <div className="mt-8 pt-8 border-t border-slate-50 flex flex-col gap-4">
                    <button
                        onClick={handleMagicLink}
                        disabled={loading}
                        className="text-[10px] font-black text-indigo-600 hover:text-indigo-700 uppercase tracking-widest underline underline-offset-4 decoration-2"
                    >
                        Send Magic Login Link
                    </button>
                    <p className="text-[9px] text-slate-400 font-medium px-8 leading-relaxed">
                        Unauthorized access is monitored. Access restricted to registered UDAAN administrators only.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
